<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-additional-services"
    size="medium"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar este serviço adicional?') }}</p>
        <p class="text-muted">{{ itemToDelete?.so_additional_service_name }}</p>
      </div>

      <div v-else>
        <s-o-additional-services-form
          ref="formRef"
          :itemId="currentId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-additional-services-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import SOAdditionalServicesForm from './SOAdditionalServicesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm?: () => boolean;
  getItemName: () => string;
}

interface ModalOpenPayload {
  id?: Id;
  data?: Record<string, unknown> | null;
  action?: string;
}

const { t } = useI18n();
const { remove } = useSOCatalog('additional_services');

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentId = ref<Id | undefined>(undefined);
const itemToDelete = ref<Record<string, unknown> | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) return t('Deletar Serviço Adicional');
  if (isEditMode.value) return t('Editar Serviço Adicional');
  return t('Adicionar Serviço Adicional');
});

const openForCreate = (): void => {
  log('SOAdditionalServicesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = undefined;
  nextTick(() => { if (formRef.value) formRef.value.resetForm(); });
};

const openForEdit = async (id: Id): Promise<void> => {
  log('SOAdditionalServicesModal', 'openForEdit', 'Opening modal for edit', id);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentId.value = id;
  await nextTick();
  if (formRef.value) await formRef.value.loadForEdit();
};

const openForDelete = async (id: Id, data: Record<string, unknown> | null = null): Promise<void> => {
  log('SOAdditionalServicesModal', 'openForDelete', 'Opening modal for delete', id);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentId.value = id;
  itemToDelete.value = data;
};

const enterDeleteConfirmation = (): void => {
  log('SOAdditionalServicesModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentId.value);
  itemToDelete.value = { so_additional_service_name: formRef.value?.getItemName() ?? '' };
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleClose = (): void => {
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = undefined;
  itemToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;
  if (formRef.value) formRef.value.resetForm();
};

const handleSave = async (): Promise<void> => {
  if (formRef.value) {
    isSaving.value = true;
    try { await formRef.value.handleSubmit(); } finally { isSaving.value = false; }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentId.value) return;
  isDeleting.value = true;
  try {
    await remove({ so_additional_service_id: currentId.value });
    eventBus.emit('globalMessage.success', t('Serviço adicional deletado com sucesso'));
    eventBus.emit('so-additional-services.deleted');
    handleClose();
  } catch (error) {
    logError('SOAdditionalServicesModal', 'handleDelete', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar serviço adicional') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  eventBus.emit('so-additional-services.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOAdditionalServicesModal', 'handleFormError', 'Form error', error);
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOAdditionalServicesModal', 'handleModalOpen', 'Modal open event', data);
  if (data?.action === 'delete' && data?.id) await openForDelete(data.id, data.data || null);
  else if (data?.id) await openForEdit(data.id);
  else openForCreate();
};

onMounted(() => {
  eventBus.on('modal.so-additional-services.open', handleModalOpen);
  eventBus.on('modal.so-additional-services.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.so-additional-services.open', handleModalOpen);
  eventBus.off('modal.so-additional-services.close', handleClose);
});

defineExpose({ openForCreate, openForEdit, openForDelete });
</script>

<style scoped>
.delete-confirmation { padding: 20px; text-align: center; }
.delete-confirmation p { margin-bottom: 10px; font-size: 16px; }
.text-muted { color: #6c757d; font-style: italic; }
.delete-action { display: flex; justify-content: flex-end; margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--app-bg-dark, #ccc); }
</style>
